import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { GAME_STATES, PLANET, PLAYER, SHIP, REPAIR_TOOLS, HARMONY, CREATURE_BODY_CONFIG } from './utils/constants.js';
import { assetLoader } from './utils/AssetLoader.js';

import { events, EVT } from './utils/events.js';
import { GalaxyGenerator } from './galaxy/GalaxyGenerator.js';
import { Starfield } from './galaxy/Starfield.js';
import { PlanetRenderer } from './planet/PlanetRenderer.js';
import { RockFormation } from './planet/RockFormation.js';
import { SurfaceScatter } from './planet/SurfaceScatter.js';
import { SurfacePlan } from './planet/SurfacePlan.js';
import { CreatureGenerator } from './creatures/CreatureGenerator.js';
import { animateCreature, triggerSing } from './creatures/CreatureMeshBuilder.js';
import { StorySites } from './world/StorySites.js';
import { listSingingPlanets } from './firebase.js';
import { FlockingSystem } from './creatures/FlockingSystem.js';
import { AudioEngine } from './audio/AudioEngine.js';
import { ShipModel } from './ship/ShipModel.js';
import { ShipController } from './ship/ShipController.js';
import { PlayerController } from './player/PlayerController.js';
import { GameState } from './player/GameState.js';
import { InputManager } from './input/InputManager.js';
import { UIManager } from './ui/UIManager.js';
import { GalaxyMap } from './ui/GalaxyMap.js';
import { Minimap } from './ui/Minimap.js';
import { MusicMaker } from './ui/MusicMaker.js';
import { PlanetStreamer } from './game/PlanetStreamer.js';
import { MobileControls } from './game/MobileControls.js';
import { hasProfanity } from './utils/profanity.js';

/**
 * ╔══════════════════════════════════════════════╗
 *  COSMIC SYMPHONY — Main Game Entry
 * ╚══════════════════════════════════════════════╝
 */

class Game {
  constructor() {
    this.state = GAME_STATES.LOADING;

    // Three.js
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.clock = new THREE.Clock();
    this.activePlanetDetailLevel = null;

    // Systems
    this.galaxy = new GalaxyGenerator(42);
    this.planetRenderer = new PlanetRenderer();
    this.rockFormation = new RockFormation();
    this.surfaceScatter = new SurfaceScatter();
    this.storySites = new StorySites();
    this.creatureGen = new CreatureGenerator();
    this.flockingSystem = new FlockingSystem();
    this.audio = new AudioEngine();
    this.input = new InputManager();
    this.gameState = new GameState();

    // Ship
    this.shipModel = new ShipModel();
    this.shipController = null;

    // Player
    this.playerController = null;

    // UI
    this.ui = null;
    this.galaxyMap = null;
    this.minimap = null;
    this.musicMaker = new MusicMaker();

    // Scene objects
    this.starfield = null;
    this.currentPlanetGroup = null;
    this.currentStarMesh = null;
    this.creatureMeshes = [];
    this.creatureData = [];

    // System-wide planet markers (simple spheres for non-active planets)
    this.systemPlanetMeshes = [];   // { mesh, planet, worldPos }
    this.activePlanetWorldPos = new THREE.Vector3(); // where the active planet sits
    this.nearestFlyablePlanet = null; // planet data of the closest non-active planet while flying
    this.nearestFlyableDist = Infinity;

    // Current location
    this.currentSystem = null;
    this.currentPlanet = null;
    this.focusedPlanet = null;

    // Warp
    this.warpProgress = 0;
    this.warpTarget = null;
    this.warpOverlay = null;
    this.preMapState = null;
  }

  async init() {
    // ── Renderer ──
    const isMobile = 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
    this.renderer = new THREE.WebGLRenderer({
      antialias: !isMobile,          // MSAA can cause iOS WebGL context creation to fail
      alpha: false,                  // transparent canvas fails to composite on iOS Safari
      powerPreference: 'high-performance',
      failIfMajorPerformanceCaveat: false,
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 2 : 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = isMobile ? THREE.BasicShadowMap : THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    const container = document.getElementById('game-container');
    container.appendChild(this.renderer.domElement);

    // Handle WebGL context loss (common on iOS due to GPU memory pressure)
    this.renderer.domElement.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      console.warn('[Game] WebGL context lost');
    });
    this.renderer.domElement.addEventListener('webglcontextrestored', () => {
      console.log('[Game] WebGL context restored');
    });

    // Re-acquire pointer lock on click if it was lost (e.g. focus change / Esc).
    this.renderer.domElement.addEventListener('click', () => {
      const canLock = (
        this.state === GAME_STATES.ON_PLANET ||
        this.state === GAME_STATES.IN_SHIP ||
        this.state === GAME_STATES.IN_COCKPIT ||
        this.state === GAME_STATES.FLYING
      );
      if (canLock && !this.input.isPointerLocked) {
        this.input.requestPointerLock(this.renderer.domElement);
      }
    });

    // ── Scene ──
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x000011);
    this.scene.fog = new THREE.FogExp2(0x000011, 0.00003);

    // ── Camera ──
    this.camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 50000);
    this.camera.position.set(0, 5, 15);

    // ── Lighting ──
    // Cinematic three-point-ish rig: warm key (sun), cool sky/ground fill,
    // faint cool ambient floor so shadows never crush to pure black.
    const ambient = new THREE.AmbientLight(0x2a3555, 0.35);
    this.scene.add(ambient);

    this.sunLight = new THREE.DirectionalLight(0xfff1dd, 1.6);
    this.sunLight.position.set(100, 100, 50);
    this.sunLight.castShadow = true;
    const shadowRes = isMobile ? 1024 : 2048;
    this.sunLight.shadow.mapSize.set(shadowRes, shadowRes);
    this.sunLight.shadow.camera.far = 500;
    this.sunLight.shadow.camera.left = -100;
    this.sunLight.shadow.camera.right = 100;
    this.sunLight.shadow.camera.top = 100;
    this.sunLight.shadow.camera.bottom = -100;
    this.sunLight.shadow.bias = -0.0005;
    this.sunLight.shadow.normalBias = 0.02;
    this.scene.add(this.sunLight);

    const hemiLight = new THREE.HemisphereLight(0x7090cc, 0x2a1f3d, 0.4);
    this.scene.add(hemiLight);

    // Cool rim light opposite the sun — separates silhouettes from the void
    this.rimLight = new THREE.DirectionalLight(0x5577ff, 0.35);
    this.rimLight.position.set(-80, 40, -60);
    this.scene.add(this.rimLight);

    this._isMobile = isMobile;

    // ── Post-processing (desktop only — bloom is too heavy for mobile GPUs) ──
    this.composer = null;
    if (!isMobile) {
      this.composer = new EffectComposer(this.renderer);
      this.composer.addPass(new RenderPass(this.scene, this.camera));
      this.bloomPass = new UnrealBloomPass(
        new THREE.Vector2(window.innerWidth, window.innerHeight),
        0.35,   // strength — subtle glow on emissives/stars, not a haze bath
        0.6,    // radius
        0.85,   // threshold — only genuinely bright pixels bloom
      );
      this.composer.addPass(this.bloomPass);
      this.composer.addPass(new OutputPass());
    }

    // ── Starfield ──
    this.starfield = new Starfield(this.scene);

    // ── Ship ──
    this.scene.add(this.shipModel.group);
    this.shipController = new ShipController(this.shipModel, this.camera);

    // ── Player ──
    this.playerController = new PlayerController(this.camera, this.scene);

    // ── UI ──
    this.ui = new UIManager(this.gameState);
    this.galaxyMap = new GalaxyMap(this.galaxy, this.gameState);

    // ── Minimap ──
    this.minimap = new Minimap();

    // ── Cached HUD elements (avoid per-frame getElementById in the game loop) ──
    this._hudEls = {
      jetpackFill: document.getElementById('jetpack-fuel-fill'),
      jetpackFuel: document.getElementById('jetpack-fuel'),
      jetpackLabel: document.getElementById('jetpack-label'),
      journalPanel: document.getElementById('journal-panel'),
      toolsPanel: document.getElementById('tools-panel'),
    };

    // ── Crosshair ──
    this.crosshair = document.createElement('div');
    this.crosshair.className = 'crosshair hidden';
    document.body.appendChild(this.crosshair);

    // ── Warp overlay ──
    this.warpOverlay = document.createElement('div');
    this.warpOverlay.style.cssText = 'position:fixed;inset:0;z-index:300;background:white;opacity:0;pointer-events:none;transition:opacity 0.5s;';
    document.body.appendChild(this.warpOverlay);

    // ── Events ──
    this._bindEvents();

    if (this.input.isMobile()) {
      document.documentElement.classList.add('mobile', 'landscape');
      this.mobileControls = new MobileControls(this);
      this.mobileControls._setupMobileControls();
    }

    this._updateViewportHeight();

    // ── Resize ──
    window.addEventListener('resize', () => {
      this._updateViewportHeight();
      this._onResize();
    });

    // ── Loading ──
    await this.gameState.loadPlanetFromFirebase();

    // Map other players' planets into the galaxy as singing stars
    try {
      const singingDocs = await listSingingPlanets(24);
      this.gameState.assignSingingPlanets(singingDocs, this.galaxy);
    } catch (e) {
      console.warn('[Game] singing star mapping failed', e);
    }
    this.planetStreamer = new PlanetStreamer(this);
    await this.planetStreamer._loadInitialSystem();

    // ── Asset Preloading ──
    const modelUrls = Object.values(CREATURE_BODY_CONFIG).map(c => c.model).filter(u => !!u);
    if (modelUrls.length > 0) {
      document.querySelector('.loader-status').textContent = 'Loading models...';
      await assetLoader.preloadModels(modelUrls);
    }

    // Update loading UI
    document.querySelector('.loader-fill').style.width = '100%';
    document.querySelector('.loader-status').textContent = 'Ready';
    const startBtn = document.getElementById('btn-start');
    startBtn.classList.remove('hidden');

    startBtn.addEventListener('click', () => this._startGame());
  }

  _updateViewportHeight() {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  }

  _bindEvents() {
    events.on(EVT.WARP_START, (system) => this._startWarp(system));

    // Creature call/response → sing pulse + music note sprite (audible only)
    events.on(EVT.CREATURE_CALL, (data) => {
      if (!data.creature) return;
      const mesh = this.creatureMeshes?.find(m => m.userData.creatureData?.id === data.creature.id);
      if (mesh) triggerSing(mesh, data.velocity ?? 1);
      if (this.audio?.isCreatureAudible(data.creature)) {
        this._spawnMusicNote(data.creature.id);
      }
    });

    events.on(EVT.MAP_OPEN, () => {
      // Track where we came from so we can return
      if (this.state !== GAME_STATES.GALAXY_MAP) {
        this.preMapState = this.state;
      }
      this.input.exitPointerLock();
      this.input.setEnabled(false);
      this.ui.updateActionButton('EXIT');
      this.state = GAME_STATES.GALAXY_MAP;
    });
    events.on(EVT.MAP_CLOSE, () => {
      if (this.state === GAME_STATES.GALAXY_MAP) {
        this.input.setEnabled(true);
        const returnTo = this.preMapState || GAME_STATES.IN_SHIP;
        this.preMapState = null;
        switch (returnTo) {
          case GAME_STATES.IN_COCKPIT:
            this._enterCockpitMode();
            break;
          case GAME_STATES.IN_SHIP:
            this._enterShipMode();
            break;
          case GAME_STATES.ON_PLANET:
            this.state = GAME_STATES.ON_PLANET;
            this.input.requestPointerLock(this.renderer.domElement);
            break;
          case GAME_STATES.FLYING:
            this.state = GAME_STATES.FLYING;
            this.input.requestPointerLock(this.renderer.domElement);
            break;
          default:
            this._enterShipMode();
        }
      }
    });

    // Galaxy map selection → warp target
    document.addEventListener('galaxy-map-select', (e) => {
      this.ui.setWarpTarget(e.detail);
      if (this.state === GAME_STATES.GALAXY_MAP) {
        this.ui.updateActionButton(e.detail ? 'HOP' : 'EXIT');
      }
    });

    // ── Narrative event handlers ──
    events.on(EVT.SPECIES_LISTENED, ({ speciesId }) => {
      const entry = this.gameState.getSpeciesEntry(speciesId);
      if (entry) {
        events.emit(EVT.NOTIFICATION, `Documented: ${entry.name}`);
        this._updateQuestProgress();
      }
    });

    events.on(EVT.SPECIES_ALL_HEARD, () => {
      events.emit(EVT.NOTIFICATION, 'All species documented! The planet sings for you.');
    });

    events.on(EVT.TOOL_COLLECTED, ({ toolName }) => {
      events.emit(EVT.NOTIFICATION, `Found: ${toolName}`);
      this._updateQuestProgress();
    });

    events.on(EVT.ALL_TOOLS, () => {
      events.emit(EVT.NOTIFICATION, 'All repair tools collected! Return to the ship radio.');
    });

    events.on(EVT.RADIO_REPAIRED, () => {
      this._updateQuestProgress();
      this._startEndgameSequence();
    });

    events.on(EVT.MUSIC_MAKER_SAVE, () => {
      this._updateQuestProgress();
    });

    events.on(EVT.JOURNAL_OPEN, () => {
      this._openJournal();
    });

    events.on(EVT.JOURNAL_CLOSE, () => {
      this._closeJournal();
    });

    events.on('tools:open', () => {
      this._openToolsPanel();
    });

    events.on('tools:close', () => {
      this._closeToolsPanel();
    });
  }

  _startGame() {
    // Fade out loading screen
    document.getElementById('loading-screen').classList.add('fade-out');
    setTimeout(() => {
      document.getElementById('loading-screen').style.display = 'none';
    }, 1000);

    // Initialize species journal from generated creatures
    if (this._allCreatureData) {
      this.gameState.initSpeciesJournal(this._allCreatureData);
    }

    // If player has already completed intro, go straight to planet
    if (this.gameState.hasCompletedIntro && this.gameState.playerName) {
      this._beginPlanetExploration();
    } else {
      this._showIntroScreen();
    }

    // Start game loop (runs regardless of state)
    this._gameLoop();
  }

  _showIntroScreen() {
    const introScreen = document.getElementById('intro-screen');
    introScreen.classList.remove('hidden');
    this.state = GAME_STATES.INTRO;

    const nameInput = document.getElementById('astronaut-name');
    const beginBtn = document.getElementById('btn-begin');

    // Restore name if one was partially entered before
    if (this.gameState.playerName) {
      nameInput.value = this.gameState.playerName;
    }

    const onBegin = () => {
      const name = nameInput.value.trim();
      if (!name || hasProfanity(name)) {
        if (hasProfanity(name)) {
          nameInput.value = '';
          nameInput.placeholder = 'Pick a friendlier name…';
        }
        nameInput.focus();
        nameInput.style.borderColor = 'rgba(255,100,100,0.6)';
        setTimeout(() => nameInput.style.borderColor = '', 500);
        return;
      }
      this.gameState.setPlayerName(name);

      introScreen.classList.add('fade-out');
      setTimeout(() => {
        introScreen.style.display = 'none';
        // Start audio on user gesture (button click)
        this.audio.init().then(() => {
          const activeCreatures = this.creatureData.filter(c => !c.collected);
          this.audio.startPlanetSymphony(this.currentPlanet, activeCreatures);
        });
        this._beginPlanetExploration();
      }, 1200);

      beginBtn.removeEventListener('click', onBegin);
    };

    beginBtn.addEventListener('click', onBegin);
    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') onBegin();
    });

    setTimeout(() => nameInput.focus(), 500);
  }

  _beginPlanetExploration() {
    // Start audio if not already started (returning player)
    if (!this.audio.started) {
      this.audio.init().then(() => {
        const activeCreatures = this.creatureData.filter(c => !c.collected);
        this.audio.startPlanetSymphony(this.currentPlanet, activeCreatures);
      });
    }

    // Enter planet surface mode
    this._enterPlanetMode();

    // Show HUD
    this.ui.showHUD();
    this._updateQuestProgress();

    events.emit(EVT.NOTIFICATION, `Welcome, ${this.gameState.playerName}. Document the creatures. Repair the radio.`);
  }

  _enterPlanetMode() {
    this.state = GAME_STATES.ON_PLANET;
    this.playerController.enable();

    // Only turn off broadcast mode if the radio hasn't been repaired.
    // When the radio is working, broadcast persists across state changes
    // so the player hears the full planet symphony everywhere.
    if (this.audio._broadcastMode && !this.gameState.radioRepaired) {
      this.audio._broadcastMode = false;
      const indicator = document.getElementById('broadcast-indicator');
      if (indicator) indicator.classList.add('hidden');
    }

    const planetRadius = PLANET.SURFACE_RADIUS * this.currentPlanet.size;
    this.playerController.setPlanet(new THREE.Vector3(0, 0, 0), planetRadius);

    // Home planet must be known before the surface plan is built so the
    // story-site clearings get reserved on it.
    if (this.gameState.homePlanetSeed == null) {
      this.gameState.setHomePlanet(this.currentPlanet.seed);
    }
    const isHomePlanet = this.currentPlanet.seed === this.gameState.homePlanetSeed;

    // Shared zoning plan — every scatter system places through it, so ruins,
    // rock outcrops, crystal fields, geyser basins, and groves never collide.
    const surfacePlan = new SurfacePlan(this.currentPlanet, planetRadius, {
      reserveStorySites: isHomePlanet,
    });

    // Generate rock outcrops on the planet surface
    this.rockFormation.clear();
    const heightFn = (dir) => this.planetRenderer.getTerrainHeight(
      this.currentPlanet.terrain, this.currentPlanet.seed, planetRadius, dir,
    );
    const { group: rockGroup } = this.rockFormation.generateRocks(
      this.currentPlanet, planetRadius, heightFn, surfacePlan,
    );
    this.currentPlanetGroup.add(rockGroup);

    // ── Generate surface scatter (trees, foliage, crystals, anomalies, geysers) ──
    this.surfaceScatter.clear();
    const scatterResult = this.surfaceScatter.generate(
      this.currentPlanet, planetRadius, heightFn, surfacePlan,
    );
    this.currentPlanetGroup.add(scatterResult.group);

    // ── Hand-crafted story sites — home planet only ──
    // The first planet the player lands on becomes "home": the crash site,
    // KIP-3, and the Song Circle live there permanently.
    this.storySites.clear();
    if (isHomePlanet) {
      this.currentPlanetGroup.add(
        this.storySites.build(this.currentPlanet, planetRadius, heightFn),
      );
    }

    this.playerController.setSurfaceHeightResolver((normalizedDir) => {
      return this.planetRenderer.getTerrainHeight(
        this.currentPlanet.terrain,
        this.currentPlanet.seed,
        planetRadius,
        normalizedDir,
      );
    });

    // Place player near the ship (slightly offset so they can see it)
    const spawnDir = this.spawnDir || new THREE.Vector3(0, 1, 0);
    // Offset the player a bit from the ship direction
    const playerDir = spawnDir.clone().applyAxisAngle(new THREE.Vector3(1, 0, 0), 0.05).normalize();
    const surfaceH = this.planetRenderer.getTerrainHeight(
      this.currentPlanet.terrain, this.currentPlanet.seed, planetRadius, playerDir
    );
    this.playerController.position.copy(playerDir.clone().multiplyScalar(surfaceH + PLAYER.HEIGHT));
    this.playerController.gravityDir.copy(playerDir).negate();
    this.camera.position.copy(this.playerController.position);

    // Compute initial yaw toward the ship in the local surface frame
    this.playerController.yaw = 0;
    this.playerController.pitch = 0;
    this.playerController._buildLocalFrame();
    const toShip = this.shipModel.group.position.clone()
      .sub(this.playerController.position).normalize();
    // Project toShip onto the surface plane
    const localUp = this.playerController._localUp;
    toShip.sub(localUp.clone().multiplyScalar(toShip.dot(localUp))).normalize();
    // Angle between localForward and toShip around localUp
    const fwd = this.playerController._localForward;
    const rgt = this.playerController._localRight;
    this.playerController.yaw = Math.atan2(-toShip.dot(rgt), toShip.dot(fwd));

    this.crosshair.classList.remove('hidden');
    this.input.requestPointerLock(this.renderer.domElement);
    this.planetStreamer._setActivePlanetDetail(false);

    // Ensure flocking + call/response are synced to the current active planet.
    this.planetStreamer._syncActivePlanetFlocking();

    // Configure minimap for this planet
    const planetCol = new THREE.Color();
    planetCol.setHSL(
      this.currentPlanet.visual.hue,
      this.currentPlanet.visual.saturation,
      this.currentPlanet.visual.lightness
    );
    this.minimap.setPlanet(this.activePlanetWorldPos, planetRadius, planetCol);
    this.minimap.show();

    this.ui.updateControlsHint('WASD Move · SHIFT Sprint · SPACE Jump · E Interact · TAB Journal');
    this.audio.setDistanceFade(0);
    this.audio.fadeIn(0.5);
    const activeCreatures = this.creatureData.filter(c => !c.collected);
    this.audio.startPlanetSymphony(this.currentPlanet, activeCreatures).catch(() => {});
    events.emit(EVT.PLANET_ENTER, this.currentPlanet);
  }

  _enterShipMode() {
    this.state = GAME_STATES.IN_SHIP;
    this.playerController.disable();
    this.shipController.enterShip();
    this.minimap.hide();

    // Show interior (flight mode hides it)
    this.shipModel.showInterior(true);

    // Place camera at the interior spawn point, looking toward cockpit
    this.ship = this.shipModel;
    const spawnWorld = this.shipModel.getInteriorSpawnWorld();
    this.camera.position.copy(spawnWorld);

    // Re-enable player controller for walking inside
    this.playerController.enable();
    this.playerController.position.copy(spawnWorld);
    // Inside the ship, gravity is simple downward in ship-local space
    const shipUp = new THREE.Vector3(0, 1, 0).applyQuaternion(this.shipModel.group.quaternion);
    this.playerController.gravityDir.copy(shipUp).negate();
    // Override planet so ground collision works at the ship floor level
    this.playerController.setPlanet(
      this.shipModel.group.position.clone().sub(shipUp.clone().multiplyScalar(100)),
      100 // radius so surface = floor
    );
    this.playerController.setSurfaceHeightResolver(null);

    // Compute yaw toward cockpit in the ship's local frame
    this.playerController.yaw = 0;
    this.playerController.pitch = 0;
    this.playerController._buildLocalFrame();
    const cockpitDir = this.shipModel.getPilotSeatWorld().clone().sub(spawnWorld).normalize();
    const sUp = this.playerController._localUp;
    cockpitDir.sub(sUp.clone().multiplyScalar(cockpitDir.dot(sUp))).normalize();
    const sFwd = this.playerController._localForward;
    const sRgt = this.playerController._localRight;
    this.playerController.yaw = Math.atan2(-cockpitDir.dot(sRgt), cockpitDir.dot(sFwd));

    this.crosshair.classList.remove('hidden');
    this.input.requestPointerLock(this.renderer.domElement);

    this.ui.updateControlsHint('WASD Walk · E Interact · Look at radio to repair · ESC Exit Ship');
  }

  _enterCockpitMode() {
    this.state = GAME_STATES.IN_COCKPIT;
    this.playerController.disable();
    this.shipController.sitInCockpit();

    this.crosshair.classList.remove('hidden');
    this.ui.hideInteractPrompt();
    if (this.gameState.radioRepaired) {
      this.ui.updateControlsHint('SPACE Launch · C Console · ESC Leave Cockpit');
      this.ui.updateActionButton('LAUNCH');
    } else {
      this.ui.updateControlsHint('Repair the radio before launch · ESC Leave Cockpit');
      this.ui.updateActionButton('LOCKED');
    }
  }

  _enterFlyingMode() {
    this.state = GAME_STATES.FLYING;
    this.playerController.disable();
    this.shipController.launch(new THREE.Vector3(0, 0, 0));
    this.shipModel.showInterior(false);
    this.minimap.hide();

    // Ensure audio is at full vol when launching (distance fade will take over)
    this.audio.fadeIn(0.3);

    this.crosshair.classList.remove('hidden');
    this.ui.updateControlsHint('WASD Steer · SPACE Throttle · SHIFT Boost · Q/E Roll · L Land · C Console');
  }

  async _startWarp(targetSystem) {
    if (!targetSystem) return;
    this.state = GAME_STATES.WARPING;
    this.warpTarget = targetSystem;
    this.warpProgress = 0;

    this.ui.hideConsole();
    events.emit(EVT.NOTIFICATION, `Warping to ${targetSystem.name}…`);
    this.audio.fadeOut(1);

    // Flash to white
    this.warpOverlay.style.opacity = '1';

    setTimeout(async () => {
      await this.planetStreamer._enterSystem(targetSystem, 0);

      // Start new symphony
      const activeCreatures = this.creatureData.filter(c => !c.collected);
      this.audio.startPlanetSymphony(this.currentPlanet, activeCreatures);
      this.audio.fadeIn(2);

      // Fade back from white
      this.warpOverlay.style.opacity = '0';

      // Re-enable input and place player on planet
      this.input.setEnabled(true);
      this._enterPlanetMode();

      events.emit(EVT.WARP_END, targetSystem);
      events.emit(EVT.NOTIFICATION, `Arrived at ${targetSystem.name}`);
    }, SHIP.WARP_DURATION * 1000);
  }

  _handleInteraction(actionPressed) {
    const pc = this.playerController;
    if (!pc.interactionType) {
      if (!pc.onGround) {
        this.ui.hideInteractPrompt();
        this.ui.showActionButton('BOOST');
      } else {
        this.ui.hideInteractPrompt();
        this.ui.hideActionButton();
      }
      return;
    }

    let actionLabel = 'A';

    switch (pc.interactionType) {
      case 'ship':
        actionLabel = 'ENTER';
        this.ui.showInteractPrompt('[E] Enter Ship');
        if (actionPressed) {
          this._enterShipMode();
        }
        break;

      case 'creature': {
        const target = pc.interactionTarget;
        const creatureId = target?.name?.replace('creature-', '');
        const creature = this.creatureData.find(c => c.id === creatureId);
        if (creature) {
          const entry = this.gameState.getSpeciesEntry(creature.speciesId);
          const listened = entry?.listened;
          if (listened) {
            actionLabel = 'LOG';
            const rl = HARMONY.INSTRUMENT_ROLES.find(r => r.id === creature.voice?.instrumentRole)?.label
              || creature.voice?.instrumentRole || '?';
            this.ui.showInteractPrompt(`${creature.name} · ${rl}`);
          } else {
            actionLabel = 'LISTEN';
            this.ui.showInteractPrompt(`Stay near to listen and document this species…`);
          }
        }
        break;
      }

      case 'console':
        if (!this.gameState.radioRepaired) {
          actionLabel = 'LOCKED';
          this.ui.showInteractPrompt('Repair the radio to enable Navigation Console');
          break;
        }
        actionLabel = 'OPEN';
        this.ui.showInteractPrompt('[E] Open Navigation Console');
        if (actionPressed) {
          events.emit(EVT.MAP_OPEN);
        }
        break;

      case 'story': {
        const charId = pc.interactionTarget?.name?.replace('story-', '');
        const character = this.storySites.getCharacter(charId);
        if (character) {
          actionLabel = 'TALK';
          this.ui.showInteractPrompt(`[E] Talk to ${character.name}`);
          if (actionPressed) {
            this.input.exitPointerLock();
            this.ui.showDialogue(character.name, character.dialogue(this.gameState)).then(() => {
              if (charId === 'kip') {
                this.gameState.markKipMet();
                this._updateQuestProgress();
              }
              this.input.requestPointerLock(this.renderer.domElement);
            });
          }
        }
        break;
      }

      case 'anomaly': {
        const target = pc.interactionTarget;
        const anomalyId = target?.name?.replace('anomaly-', '');
        const anomaly = this.surfaceScatter.getAnomalies().find(a => a.id === anomalyId);
        if (anomaly && !anomaly.interacted) {
          // Check if this ruin type has a repair tool to give
          const tool = REPAIR_TOOLS.find(t => t.ruinType === anomaly.type && !this.gameState.hasToolId(t.id));
          const label = tool ? `[E] Extract Audio Log — ${tool.icon}` : `[E] Investigate ${anomaly.type.replace(/_/g, ' ')}`;
          actionLabel = tool ? 'LOG' : 'SCAN';
          this.ui.showInteractPrompt(label);
          if (actionPressed) {
            anomaly.interacted = true;
            events.emit(EVT.ANOMALY_INTERACT, anomaly);
            if (tool) {
              const AUDIO_LOGS = {
                'antenna_coil': "We crashed... the ship is ruined. I don't know how long I can survive out here. I'm leaving these logs just in case.",
                'signal_crystal': "The creatures here... they're making sounds. At first I thought it was just noise, but it's too rhythmic. There's a pattern.",
                'circuit_board': "I think they are singing. Not just individually, but together. The entire planet is a symphony. I just need to fix the radio to hear it properly.",
                'power_cell': "I've managed to rig the radio to broadcast their frequency. I'm leaving my song for you. Just fix the ship's radio, and you'll hear it too."
              };
              
              // We want to count how many logs they've found to number them 1-4
              const logsFound = this.gameState.collectedTools.length + 1;
              const logText = AUDIO_LOGS[tool.id] || "Data corrupted.";
              
              this.input.exitPointerLock();
              this.ui.showAudioLog(logsFound, logText, tool.name, tool.icon).then(() => {
                events.emit(EVT.TOOL_COLLECTED, { toolId: tool.id, toolName: tool.name });
              });
            } else if (anomaly.loot) {
              events.emit(EVT.NOTIFICATION, `Found: ${anomaly.loot}`);
            } else {
              events.emit(EVT.NOTIFICATION, `Investigated ${anomaly.type.replace(/_/g, ' ')}`);
            }
          }
        } else if (anomaly && anomaly.interacted) {
          this.ui.showInteractPrompt('Already searched');
        }
        break;
      }
    }

    this.ui.updateActionButton(actionLabel);
  }

  _handleShipInteriorInteraction() {
    const pc = this.playerController;
    if (!pc.interactionType) {
      this.ui.hideInteractPrompt();
      this.ui.resetActionButton();
      return;
    }

    const actionPressed = this.input.justPressed('KeyE') || this.input.justPressed('Action');
    let actionLabel;

    switch (pc.interactionType) {
      case 'cockpit':
        actionLabel = 'SIT';
        this.ui.showInteractPrompt('[E] Sit in Cockpit');
        if (actionPressed) {
          this._enterCockpitMode();
        }
        break;

      case 'console':
        if (!this.gameState.radioRepaired) {
          actionLabel = 'LOCKED';
          this.ui.showInteractPrompt('Repair the radio to enable Navigation Console');
          break;
        }
        actionLabel = 'OPEN';
        this.ui.showInteractPrompt('[E] Navigation Console');
        if (actionPressed) {
          this.preMapState = GAME_STATES.IN_SHIP;
          this.input.exitPointerLock();
          this.state = GAME_STATES.GALAXY_MAP;
          events.emit(EVT.MAP_OPEN);
        }
        break;

      case 'radio': {
        if (this.gameState.radioRepaired) {
          // Radio is working — toggle broadcast or compose
          const isOn = this.audio.broadcastMode;
          actionLabel = isOn ? 'RADIO OFF' : 'RADIO ON';
          this.ui.showInteractPrompt(isOn ? '[E] Toggle Radio · [F] Compose · [R] Replay Credits' : '[E] Toggle Radio · [F] Compose · [R] Replay Credits');
          if (actionPressed) {
            const nowOn = this.audio.toggleBroadcast();
            const indicator = document.getElementById('broadcast-indicator');
            if (indicator) indicator.classList.toggle('hidden', !nowOn);
            this.shipModel.group.traverse(c => {
              if (c.name === 'radio-indicator' && c.material) {
                c.material.emissive.setHex(nowOn ? 0x22ff55 : 0x22aa55);
                c.material.emissiveIntensity = nowOn ? 1.5 : 0.6;
              }
            });
            events.emit(EVT.NOTIFICATION, nowOn ? 'Planet Radio ON' : 'Planet Radio OFF');
          }
          // F key opens the music maker from the radio
          if (this.input.justPressed('KeyF')) {
            this._openMusicMaker();
          }
          // R key replays the endgame credits
          if (this.input.justPressed('KeyR')) {
            this._replayEndgameSequence();
          }
        } else if (this.gameState.canRepairRadio()) {
          // Can repair!
          actionLabel = 'FIX';
          this.ui.showInteractPrompt('[E] Repair the Radio');
          if (actionPressed) {
            events.emit(EVT.RADIO_REPAIRED);
            events.emit(EVT.NOTIFICATION, 'The radio crackles to life…');
            // Update visual
            this.shipModel.group.traverse(c => {
              if (c.name === 'radio-indicator' && c.material) {
                c.material.emissive.setHex(0x22ff55);
                c.material.emissiveIntensity = 1.5;
              }
            });
          }
        } else {
          // Radio is broken, missing tools
          const progress = this.gameState.getToolProgress();
          actionLabel = 'RADIO';
          this.ui.showInteractPrompt(`Radio is broken. ${progress.collected}/${progress.total} repair tools found.`);
        }
        break;
      }

      case 'door':
        actionLabel = 'EXIT';
        this.ui.showInteractPrompt('[E] Exit Ship');
        if (actionPressed) {
          this.shipController.exitShip();
          this._enterPlanetMode();
        }
        break;

      default:
        this.ui.hideInteractPrompt();
        this.ui.resetActionButton();
        return;
    }

    this.ui.updateActionButton(actionLabel);
  }

  _constrainPlayerInsideShip() {
    const hits = this.shipModel.constrainInteriorPosition(this.playerController.position, 0.2);
    if (!hits.hitX && !hits.hitY && !hits.hitZ) return;

    // If we hit interior bounds, remove velocity pushing further into the wall.
    const invShipQ = this.shipModel.group.quaternion.clone().invert();
    const localVel = this.playerController.velocity.clone().applyQuaternion(invShipQ);

    if (hits.hitX) localVel.x = 0;
    if (hits.hitY) localVel.y = Math.min(0, localVel.y);
    if (hits.hitZ) localVel.z = 0;

    this.playerController.velocity.copy(localVel.applyQuaternion(this.shipModel.group.quaternion));
  }

  _updateJetpackHUD() {
    const bar = this._hudEls.jetpackFill;
    const container = this._hudEls.jetpackFuel;
    if (!bar || !container) return;

    const pc = this.playerController;
    const ratio = pc.jetpackFuel / PLAYER.JETPACK_FUEL_MAX;
    bar.style.width = `${ratio * 100}%`;

    // Color: cyan when full, yellow when low, red when empty
    if (ratio > 0.5) bar.style.background = 'var(--accent)';
    else if (ratio > 0.2) bar.style.background = 'var(--warn)';
    else bar.style.background = 'var(--danger)';

    // Show when on planet, hide otherwise
    const show = this.state === GAME_STATES.ON_PLANET;
    container.style.opacity = show ? '1' : '0';

    // Indicator label
    const label = this._hudEls.jetpackLabel;
    if (label) {
      if (pc.jetpackActive) label.textContent = 'JETPACK';
      else if (pc.boostCooldown > 0) label.textContent = 'BOOST CD';
      else label.textContent = 'FUEL';
    }
  }

  _updateCreatures(dt) {
    const time = this.clock.elapsedTime;

    // Update flocking system (handles boids movement + call-and-response).
    // Song mode follows the radio: broadcast on = perform the literal song.
    this.flockingSystem.songMode = this.audio.broadcastMode;
    this.flockingSystem.update(dt, time);

    // Procedural walk/idle animation (gait speed follows actual movement)
    this.creatureMeshes.forEach((mesh) => {
      const data = mesh.userData.creatureData;
      if (!data) return;
      // Movement speed from frame-to-frame displacement, smoothed
      const prev = mesh.userData._prevPos;
      if (prev) {
        const speed = mesh.position.distanceTo(prev) / Math.max(dt, 1e-4);
        const target = Math.min(speed / 2.5, 1); // ~2.5 u/s = full stride
        mesh.userData._speed01 = (mesh.userData._speed01 ?? 0) * 0.85 + target * 0.15;
      } else {
        mesh.userData._prevPos = new THREE.Vector3();
        mesh.userData._speed01 = 0;
      }
      mesh.userData._prevPos.copy(mesh.position);
      animateCreature(mesh, time, mesh.userData._speed01);
    });

    // Update music note sprites
    this._updateMusicNotes(dt);
  }

  /**
   * Spawn a floating music note above a creature when it sings.
   */
  _spawnMusicNote(creatureId) {
    // Find the mesh for this creature
    const mesh = this.creatureMeshes.find(m => m.userData.creatureData?.id === creatureId);
    if (!mesh) return;

    const symbols = ['♪', '♫', '♩', '𝅗𝅥'];
    const symbol = symbols[Math.floor(Math.random() * symbols.length)];

    const sprite = document.createElement('div');
    sprite.className = 'music-note-sprite';
    sprite.textContent = symbol;
    // Colour based on species
    const data = mesh.userData.creatureData;
    const color = data ? `#${new THREE.Color(data.color).getHexString()}` : '#fff';
    sprite.style.color = color;
    sprite.style.textShadow = `0 0 8px ${color}, 0 0 16px ${color}`;
    document.body.appendChild(sprite);

    this._musicNotes = this._musicNotes || [];
    this._musicNotes.push({
      el: sprite,
      mesh,
      life: 0,
      maxLife: 1.5,
      offsetY: 0,
    });
  }

  _updateMusicNotes(dt) {
    if (!this._musicNotes) return;
    const keep = [];
    for (const note of this._musicNotes) {
      note.life += dt;
      note.offsetY += dt * 40; // float upward in screen space

      if (note.life >= note.maxLife || !note.mesh) {
        note.el.remove();
        continue;
      }

      // Project creature world position to screen
      const pos = note.mesh.position.clone();
      pos.y += (note.mesh.userData.creatureData?.size || 1) * 0.8;
      pos.project(this.camera);

      // Check if behind camera
      if (pos.z > 1) {
        note.el.style.opacity = '0';
        keep.push(note);
        continue;
      }

      const x = (pos.x * 0.5 + 0.5) * window.innerWidth;
      const y = (-pos.y * 0.5 + 0.5) * window.innerHeight - note.offsetY;
      const alpha = 1 - (note.life / note.maxLife);

      note.el.style.left = `${x}px`;
      note.el.style.top = `${y}px`;
      note.el.style.opacity = `${alpha}`;
      keep.push(note);
    }
    this._musicNotes = keep;
  }

  // ═══════════════════════════════════════════════════════
  //  NARRATIVE SYSTEMS — Species Listening, Tools, Radio, Endgame, Music Maker
  // ═══════════════════════════════════════════════════════════

  /**
   * Check if the player is near a creature species they haven't listened to yet.
   * "Listening" = being within earshot for a few seconds.
   */
  _updateSpeciesListening(dt) {
    if (!this._allCreatureData || !this.gameState.speciesJournal) return;
    const playerPos = this.camera.position;
    const listenRange = 25;  // meters — close enough to hear clearly
    const listenTime = 3;    // seconds of proximity required

    if (!this._speciesListenTimers) this._speciesListenTimers = {};

    // Group creatures by species, check if any member is close
    const speciesNearby = new Set();
    for (const creature of this.creatureData) {
      if (creature.collected) continue;
      const mesh = this.creatureMeshes.find(m => m.userData.creatureData?.id === creature.id);
      if (!mesh) continue;
      const dist = playerPos.distanceTo(mesh.position);
      if (dist < listenRange) {
        speciesNearby.add(creature.speciesId);
      }
    }

    // Find the species with the highest listen progress (for the indicator)
    let bestListenSpecies = null;
    let bestProgress = 0;

    for (const speciesId of speciesNearby) {
      const entry = this.gameState.getSpeciesEntry(speciesId);
      if (!entry || entry.listened) continue;

      if (!this._speciesListenTimers[speciesId]) {
        this._speciesListenTimers[speciesId] = 0;
      }
      this._speciesListenTimers[speciesId] += dt;

      const progress = this._speciesListenTimers[speciesId] / listenTime;
      if (progress > bestProgress) {
        bestProgress = progress;
        bestListenSpecies = entry;
      }

      if (this._speciesListenTimers[speciesId] >= listenTime) {
        events.emit(EVT.SPECIES_LISTENED, { speciesId });
        delete this._speciesListenTimers[speciesId];
        bestListenSpecies = null; // just finished — hide indicator
      }
    }

    // Reset timers for species we walked away from
    for (const sid of Object.keys(this._speciesListenTimers)) {
      if (!speciesNearby.has(Number(sid))) {
        this._speciesListenTimers[sid] = Math.max(0, this._speciesListenTimers[sid] - dt * 2);
      }
    }

    // ── Update floating listen indicator ──
    const indicator = document.getElementById('listen-indicator');
    if (!indicator) return;
    if (bestListenSpecies && bestProgress > 0) {
      indicator.classList.remove('hidden');
      const ringFill = indicator.querySelector('.listen-ring-fill');
      const label = indicator.querySelector('.listen-label');
      const circumference = 100; // stroke-dasharray out of 100
      const filled = Math.min(bestProgress, 1) * circumference;
      ringFill.setAttribute('stroke-dasharray', `${filled} ${circumference - filled}`);
      label.textContent = `Listening to ${bestListenSpecies.name}…`;
    } else {
      indicator.classList.add('hidden');
    }
  }

  /**
   * Update quest progress HUD counters and the persistent objective line.
   */
  _updateQuestProgress() {
    const speciesEl = document.getElementById('quest-species');
    const toolsEl = document.getElementById('quest-tools');
    if (speciesEl) {
      const heard = this.gameState.getListenedCount();
      const total = this.gameState.allSpeciesCount;
      speciesEl.textContent = `${heard}/${total} species`;
    }
    if (toolsEl) {
      const progress = this.gameState.getToolProgress();
      toolsEl.textContent = `${progress.collected}/${progress.total} tools`;
    }
    this._updateObjectiveLine();
  }

  /**
   * One line that always tells the player where to head next.
   * Follows the story spine: campfire → beacons → radio → listening → composing.
   */
  _updateObjectiveLine() {
    const line = document.getElementById('objective-line');
    const text = document.getElementById('objective-text');
    if (!line || !text) return;

    const gs = this.gameState;
    let objective;
    // metKip is skipped once tool progress exists — saves predating the flag,
    // or players who found a beacon before the camp, shouldn't be sent back.
    if (!gs.metKip && gs.collectedTools.length === 0 && !gs.radioRepaired) {
      objective = 'Someone survived here — find the campfire smoke';
    } else if (!gs.allToolsCollected) {
      const p = gs.getToolProgress();
      objective = `Recover the radio parts beneath the light beacons (${p.collected}/${p.total})`;
    } else if (!gs.radioRepaired) {
      objective = 'Return to your ship and repair the radio';
    } else if (!gs.allSpeciesHeard) {
      objective = 'Walk among the creatures — listen to every species';
    } else if (!gs.mySongData) {
      objective = 'Visit the Song Circle, then compose your own song at the ship console';
    } else {
      objective = 'Your song is ready — carry it to another star';
    }

    text.textContent = objective;
  }

  // ── Journal ──

  _openJournal() {
    const panel = document.getElementById('journal-panel');
    const grid = document.getElementById('species-grid');
    const count = document.getElementById('journal-count');

    grid.innerHTML = '';
    const entries = Object.values(this.gameState.speciesJournal);
    const iconMap = { blob: '🫧', quadruped: '🦎', biped: '🧬', serpent: '🐍', floater: '🎈', insectoid: '🦗' };
    const roleLabel = id => HARMONY.INSTRUMENT_ROLES.find(r => r.id === id)?.label || id;

    entries.forEach(entry => {
      const card = document.createElement('div');
      card.className = `species-card ${entry.listened ? 'listened' : 'undiscovered'}`;
      card.innerHTML = `
        <div class="species-icon">${iconMap[entry.bodyType] || '👾'}</div>
        <div class="species-name">${entry.listened ? entry.name : '???'}</div>
        <div class="species-role">${entry.listened ? roleLabel(entry.instrumentRole) : '—'}</div>
        ${entry.listened ? '<div class="species-check"><svg class="icon icon-sm" aria-hidden="true"><use href="#icon-check"/></svg></div>' : ''}
      `;
      grid.appendChild(card);
    });

    const heard = entries.filter(e => e.listened).length;
    count.textContent = `${heard} / ${entries.length} species documented`;

    panel.classList.remove('hidden');
    this._preJournalState = this.state;
    this.state = GAME_STATES.JOURNAL;
    this.input.exitPointerLock();
  }

  _closeJournal() {
    document.getElementById('journal-panel').classList.add('hidden');
    const returnTo = this._preJournalState || GAME_STATES.ON_PLANET;
    this._preJournalState = null;
    this.state = returnTo;
    if (returnTo === GAME_STATES.ON_PLANET) {
      this.input.requestPointerLock(this.renderer.domElement);
    }
  }

  // ── Tools panel ──

  _openToolsPanel() {
    const panel = document.getElementById('tools-panel');
    const grid = document.getElementById('tools-grid');
    const count = document.getElementById('tools-count');

    grid.innerHTML = '';
    const progress = this.gameState.getToolProgress();

    progress.tools.forEach(tool => {
      const card = document.createElement('div');
      card.className = `tool-card ${tool.found ? 'found' : 'missing'}`;
      card.innerHTML = `
        <div class="tool-icon">${tool.icon}</div>
        <div class="tool-name">${tool.found ? tool.name : '???'}</div>
        <div class="tool-desc">${tool.found ? tool.description : 'Search the ruins…'}</div>
      `;
      grid.appendChild(card);
    });

    count.textContent = `${progress.collected} / ${progress.total} tools collected`;

    panel.classList.remove('hidden');
    this._preToolsState = this.state;
    this.state = GAME_STATES.JOURNAL;  // reuse journal state for overlay
    this.input.exitPointerLock();
  }

  _closeToolsPanel() {
    document.getElementById('tools-panel').classList.add('hidden');
    const returnTo = this._preToolsState || GAME_STATES.ON_PLANET;
    this._preToolsState = null;
    this.state = returnTo;
    if (returnTo === GAME_STATES.ON_PLANET) {
      this.input.requestPointerLock(this.renderer.domElement);
    }
  }

  // ── Endgame ──

  _startEndgameSequence() {
    this.state = GAME_STATES.ENDGAME;
    this.input.exitPointerLock();
    this.crosshair.classList.add('hidden');
    this.ui.hideHUD();
    this.minimap.hide();

    const overlay = document.getElementById('endgame-overlay');
    overlay.classList.remove('hidden');

    // Phase 1: Drone shot — slowly pull camera up and away from planet
    const startPos = this.camera.position.clone();
    this._endgamePhase = 'drone';
    this._endgameTime = 0;
    this._endgameDroneDuration = 12; // 12-second cinematic drone pull-back
    this._endgameStartPos = startPos;

    // Final position: high above the planet, slightly offset for cinematic angle
    const planetRadius = PLANET.SURFACE_RADIUS * this.currentPlanet.size;
    this._endgameFinalDist = planetRadius * 5;
    this._endgamePlanetRadius = planetRadius;

    // Enable broadcast mode — the planet's creatures ARE the previous player's
    // composition, so hearing all of them at once IS the song reveal.
    this.audio._broadcastMode = true;
    const indicator = document.getElementById('broadcast-indicator');
    if (indicator) indicator.classList.remove('hidden');

    // Ramp volume up for the reveal (creatures become fully audible)
    if (this.audio?.masterGain) {
      this.audio.fadeTo(0.7, 4);
    }

    // Populate song attribution
    const remote = this.gameState.remoteLastPlanet;
    const songTitle = remote?.songName || this.gameState.previousSongName || 'An Unknown Melody';
    const authorName = remote?.playerName || this.gameState.previousPlayerName || 'A Distant Traveler';
    let timeStr = '';
    const savedAt = remote?.savedAt;
    if (savedAt) {
      try {
        // Firestore Timestamp (has toDate) or legacy ISO string
        const d = typeof savedAt?.toDate === 'function' ? savedAt.toDate() : new Date(savedAt);
        if (!Number.isNaN(d.getTime())) {
          timeStr = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
        }
      } catch (_) {}
    }
    document.getElementById('endgame-song-title').textContent = `“${songTitle}”`;
    document.getElementById('endgame-song-author').textContent = `by ${authorName}${timeStr ? ` · ${timeStr}` : ''}`;

    // 0s — background darkens, drone begins
    overlay.classList.add('active');

    // Cancellable phase timer system
    this._endgameStep = 0;
    this._endgameTimers = [];
    const t = (fn, ms) => this._endgameTimers.push(setTimeout(fn, ms));

    t(() => this._fireEndgameStep(1), 2000);   // concept in
    t(() => this._fireEndgameStep(2), 9000);   // credits in (concept out)
    t(() => this._fireEndgameStep(3), 16000);  // prompt in
  }

  _updateEndgame(dt) {
    if (this._endgamePhase !== 'drone') return;
    this._endgameTime += dt;

    const t = Math.min(1, this._endgameTime / this._endgameDroneDuration);
    const eased = t * t * (3 - 2 * t); // smoothstep

    // Pull camera back while orbiting gently
    const orbitAngle = t * Math.PI * 0.4; // gentle 72° orbit
    const dist = THREE.MathUtils.lerp(
      this._endgameStartPos.length(),
      this._endgameFinalDist,
      eased
    );
    const height = THREE.MathUtils.lerp(
      this._endgameStartPos.y,
      this._endgamePlanetRadius * 3.5,
      eased
    );

    this.camera.position.set(
      Math.sin(orbitAngle) * dist * 0.7,
      height,
      Math.cos(orbitAngle) * dist * 0.7,
    );
    this.camera.lookAt(0, 0, 0);

    if (t >= 1) {
      this._endgamePhase = 'credits';
    }
  }

  _fireEndgameStep(step) {
    this._endgameStep = step;
    const overlay = document.getElementById('endgame-overlay');
    if (step === 1) {
      overlay.classList.add('concept-visible');
    } else if (step === 2) {
      overlay.classList.add('credits-visible'); // concept fades out via CSS
    } else if (step === 3) {
      document.getElementById('endgame-prompt').classList.remove('hidden');
      // Skip hint hides once prompt is up
      const hint = document.getElementById('endgame-skip-hint');
      if (hint) hint.style.opacity = '0';
      this._bindEndgameButtons();
    }
  }

  _advanceEndgameStep() {
    if ((this._endgameStep || 0) >= 3) return;
    // Cancel all pending auto-advances
    this._endgameTimers?.forEach(t => clearTimeout(t));
    this._endgameTimers = [];
    const next = (this._endgameStep || 0) + 1;
    this._fireEndgameStep(next);
    // Re-schedule further auto-advances from here, with natural hold times
    const hold = (fn, ms) => this._endgameTimers.push(setTimeout(fn, ms));
    if (next === 1) hold(() => this._advanceEndgameStep(), 6000);  // hold concept 6s then credits
    if (next === 2) hold(() => this._advanceEndgameStep(), 6000);  // hold credits 6s then prompt
  }

  // ── Colonize Prompt ──

  _replayEndgameSequence() {
    // Reset state so the sequence runs cleanly
    this._endgameBound = false;
    this._endgamePhase = null;
    this._endgameStep = 0;
    this._endgameTimers?.forEach(t => clearTimeout(t));
    this._endgameTimers = [];
    document.getElementById('endgame-prompt').classList.add('hidden');
    const hint = document.getElementById('endgame-skip-hint');
    if (hint) hint.style.opacity = '';
    const overlay = document.getElementById('endgame-overlay');
    overlay.classList.remove('active', 'concept-visible', 'credits-visible');
    setTimeout(() => this._startEndgameSequence(), 50);
  }

  _showColonizePrompt(planet) {
    const dialog = document.getElementById('colonize-dialog');
    if (!dialog) return;
    const nameEl = document.getElementById('colonize-planet-name');
    if (nameEl) nameEl.textContent = planet?.name || 'this planet';
    dialog.classList.remove('hidden');
    this.input.exitPointerLock();

    if (!this._colonizeBound) {
      this._colonizeBound = true;
      document.getElementById('btn-colonize-compose').addEventListener('click', () => {
        dialog.classList.add('hidden');
        this._openMusicMaker();
      });
      document.getElementById('btn-colonize-later').addEventListener('click', () => {
        dialog.classList.add('hidden');
        this.input.requestPointerLock(this.renderer.domElement);
      });
    }
  }

  _hideColonizePrompt() {
    const dialog = document.getElementById('colonize-dialog');
    if (dialog) dialog.classList.add('hidden');
  }

  _bindEndgameButtons() {
    if (this._endgameBound) return;
    this._endgameBound = true;

    const writeSongBtn = document.getElementById('btn-write-song');
    const skipBtn = document.getElementById('btn-skip-song');

    writeSongBtn.addEventListener('click', () => {
      document.getElementById('endgame-overlay').classList.add('hidden');
      // Restore audio volume for composing
      if (this.audio?.masterGain) {
        this.audio.fadeTo(0.6, 2);
      }
      this._openMusicMaker();
    });

    skipBtn.addEventListener('click', () => {
      document.getElementById('endgame-overlay').classList.add('hidden');
      // Restore audio volume
      if (this.audio?.masterGain) {
        this.audio.fadeTo(0.6, 2);
      }
      // Return to planet — free exploration
      this.ui.showHUD();
      this._enterPlanetMode();
    });
  }

  // ── Music Maker ──

  _openMusicMaker() {
    const maker = document.getElementById('music-maker');
    maker.classList.remove('hidden');
    this._preMusicMakerState = this.state;
    this.state = GAME_STATES.MUSIC_MAKER;
    this.input.exitPointerLock();

    // Gather listened species (keyed by instrument role)
    const listenedByRole = {};
    for (const entry of Object.values(this.gameState.speciesJournal)) {
      if (entry.listened) listenedByRole[entry.instrumentRole] = entry;
    }

    // Build one entry per instrument role — species info when available
    const allEntries = HARMONY.INSTRUMENT_ROLES.map(role => {
      const species = listenedByRole[role.id];
      return {
        speciesId: species?.speciesId ?? null,
        name: species?.name ?? role.label,
        instrumentRole: role.id,
        bodyType: species?.bodyType ?? null,
        discovered: !!species,
      };
    });

    // Build planet music context
    const pm = this.currentPlanet?.music || {};
    const planetMusic = {
      root: pm.root || 'C',
      scaleName: pm.scale || 'pentatonic',
      octave: pm.octave || 3,
      tempo: pm.tempo || 100,
    };

    this.musicMaker.open(allEntries, planetMusic, this.audio);

    // Load existing composition if any
    if (this.gameState.mySongData) {
      this.musicMaker.deserialize(this.gameState.mySongData);
      const nameInput = document.getElementById('mm-song-name');
      if (nameInput && this.gameState.mySongName) nameInput.value = this.gameState.mySongName;
    }

    this._bindMusicMakerControls();
  }

  _closeMusicMaker() {
    const panel = document.getElementById('music-maker');
    if (panel.classList.contains('hidden')) return; // already closed
    this.musicMaker.close();
    panel.classList.add('hidden');
    const returnTo = this._preMusicMakerState || GAME_STATES.ON_PLANET;
    this._preMusicMakerState = null;

    // If coming from endgame, go to planet
    if (returnTo === GAME_STATES.ENDGAME) {
      this.ui.showHUD();
      this._enterPlanetMode();
    } else {
      this.state = returnTo;
      if (returnTo === GAME_STATES.ON_PLANET) {
        this.input.requestPointerLock(this.renderer.domElement);
      }
    }
  }

  _bindMusicMakerControls() {
    // Prevent double-binding
    if (this._mmBound) return;
    this._mmBound = true;

    document.getElementById('mm-close').addEventListener('click', () => this._closeMusicMaker());
    document.getElementById('mm-save').addEventListener('click', async () => {
      const nameInput = document.getElementById('mm-song-name');
      const songName = nameInput.value.trim() || 'Untitled Harmony';
      if (hasProfanity(songName)) {
        events.emit(EVT.NOTIFICATION, 'Choose a friendlier song name');
        nameInput.focus();
        return;
      }

      const data = this.musicMaker.serialize();
      
      // Update local state — pass the FULL serialized song so bars/octaves/
      // loop survive a reopen (previously truncated to the 8-bar default)
      events.emit(EVT.MUSIC_MAKER_SAVE, { songName, ...data });
      // Persist to Firebase and get the unique ID
      // (savePlanetToFirebase swallows errors and returns null on failure)
      const docId = await this.gameState.savePlanetToFirebase(
        this.currentPlanet?.name,
        songName,
        data,
        this.currentPlanet?.music,
      );

      if (docId) {
        events.emit(EVT.NOTIFICATION, `Saved: "${songName}"`);
        const url = new URL(window.location.origin + window.location.pathname);
        url.searchParams.set('planet', docId);
        await this.ui.showShareDialog(url.toString());
      } else {
        events.emit(EVT.NOTIFICATION, `Saved locally: "${songName}" (cloud sync unavailable)`);
      }

      this._closeMusicMaker();
    });
  }

  // ── Minimap markers for species & ruins ──

  _updateMinimapMarkers() {
    if (!this.minimap || !this.minimap.visible) return;

    // Gather species flock positions (one per species, averaged)
    const speciesPositions = {};
    for (const creature of this.creatureData) {
      if (creature.collected) continue;
      const mesh = this.creatureMeshes.find(m => m.userData.creatureData?.id === creature.id);
      if (!mesh) continue;
      if (!speciesPositions[creature.speciesId]) {
        speciesPositions[creature.speciesId] = { pos: new THREE.Vector3(), count: 0, listened: false };
      }
      speciesPositions[creature.speciesId].pos.add(mesh.position);
      speciesPositions[creature.speciesId].count++;
      const entry = this.gameState.getSpeciesEntry(creature.speciesId);
      if (entry?.listened) speciesPositions[creature.speciesId].listened = true;
    }

    const speciesMarkers = Object.values(speciesPositions).map(s => ({
      position: s.pos.divideScalar(s.count),
      listened: s.listened,
    }));

    // Gather ruin positions
    const ruinMarkers = this.surfaceScatter.getAnomalies().map(a => ({
      position: new THREE.Vector3(a.position.x, a.position.y, a.position.z),
      interacted: a.interacted || false,
    }));

    this.minimap.setMarkers(speciesMarkers, ruinMarkers);
  }

  _gameLoop() {
    requestAnimationFrame(() => this._gameLoop());
    const dt = Math.min(this.clock.getDelta(), 0.05);
    this.gameState.stats.playSeconds += dt;

    // Update starfield
    this.starfield.update(this.camera.position);

    // Keep active planet static in world space.
    // Rotating terrain while the player is grounded feels disorienting.

    // State-specific updates
    switch (this.state) {
      case GAME_STATES.ON_PLANET: {
        const { dx, dy } = this.input.consumeMouse();
        this.playerController.handleMouseMove(dx, dy);
        this.playerController.update(dt, this.input);
        const actionPressed = this.input.justPressed('KeyE') || this.input.justPressed('Action');
        this._handleInteraction(actionPressed);
        this._updateCreatures(dt);

        // ── Species listening ──
        this._updateSpeciesListening(dt);

        // Feed player position to audio engine for spatial audio
        this.audio.setPlayerPosition(this.camera.position);

        // Update surface scatter (geysers, floating artifacts, etc.)
        this.surfaceScatter.update(dt, this.clock.elapsedTime);

        // Update story sites (campfire flicker, NPC idle + head tracking)
        this.storySites.update(dt, this.clock.elapsedTime, this.camera.position);

        // Update minimap
        this.minimap.update(
          this.playerController.position,
          this.shipModel.group.position,
          this.playerController.getFacingDirection()
        );
        this._updateMinimapMarkers();

        // Update jetpack fuel bar
        this._updateJetpackHUD();

        // ── Journal (Tab key) ──
        if (this.input.justPressed('Tab')) {
          events.emit(EVT.JOURNAL_OPEN);
        }

        break;
      }

      case GAME_STATES.IN_SHIP: {
        // Walking around inside the ship
        const { dx, dy } = this.input.consumeMouse();
        this.playerController.handleMouseMove(dx, dy);
        this.playerController.update(dt, this.input);

        // Keep player inside the ship interior volume (no clipping through hull)
        this._constrainPlayerInsideShip();

        // Creatures keep playing outside while you're in the ship
        this._updateCreatures(dt);

        // Keep spatial audio anchored to the current listener position.
        this.audio.setPlayerPosition(this.camera.position);

        // Check what the player is looking at inside the ship
        this._handleShipInteriorInteraction();

        // Exit ship back to planet
        if (this.input.justPressed('Escape')) {
          // Close colonize dialog if open, otherwise exit ship
          const colonizeOpen = !document.getElementById('colonize-dialog')?.classList.contains('hidden');
          if (colonizeOpen) {
            this._hideColonizePrompt();
            this.input.requestPointerLock(this.renderer.domElement);
          } else {
            this.shipController.exitShip();
            this._enterPlanetMode();
          }
        }
        break;
      }

      case GAME_STATES.IN_COCKPIT: {
        this.input.consumeMouse(); // consume to prevent accumulation

        const actionPressed = this.input.justPressed('KeyE') || this.input.justPressed('Action');

        // Launch into flight (only if radio is repaired)
        if (actionPressed || this.input.justPressed('Space')) {
          if (!this.gameState.radioRepaired) {
            events.emit(EVT.NOTIFICATION, 'The ship can\'t fly until the radio is repaired. Find tools in the ruins.');
          } else {
            this._enterFlyingMode();
          }
        }

        // Open console (galaxy map) from cockpit
        if (this.input.justPressed('KeyC')) {
          if (!this.gameState.radioRepaired) {
            events.emit(EVT.NOTIFICATION, 'Repair the radio to enable Navigation Console');
          } else {
            this.preMapState = GAME_STATES.IN_COCKPIT;
            this.input.exitPointerLock();
            this.state = GAME_STATES.GALAXY_MAP;
            events.emit(EVT.MAP_OPEN);
          }
        }

        // Leave cockpit back to ship interior
        if (this.input.justPressed('Escape')) {
          this.shipController.leaveCockpit();
          this._enterShipMode();
        }
        break;
      }

      case GAME_STATES.FLYING: {
        const { dx, dy } = this.input.consumeMouse();
        this.shipController.handleInput(this.input, { dx, dy });
        this.shipController.update(dt);
        this._updateCreatures(dt);

        // Keep spatial audio anchored to the current listener position.
        this.audio.setPlayerPosition(this.camera.position);

        // ── Distance-based music fade ──
        const shipPos = this.shipModel.group.position;
        const distToActivePlanet = shipPos.distanceTo(this.activePlanetWorldPos);
        const activePlanetRadius = PLANET.SURFACE_RADIUS * this.currentPlanet.size;
        this.planetStreamer._setActivePlanetDetail(distToActivePlanet > activePlanetRadius * 3);

        // ── Find nearest planet (for landing target / focus) ──
        this.nearestFlyablePlanet = null;
        this.nearestFlyableDist = Infinity;

        // Check distance to active planet
        if (distToActivePlanet < activePlanetRadius * 3) {
          this.nearestFlyablePlanet = this.currentPlanet;
          this.nearestFlyableDist = distToActivePlanet;
        }

        // Check other planets
        for (const entry of this.systemPlanetMeshes) {
          const d = shipPos.distanceTo(entry.worldPos);
          const r = PLANET.SURFACE_RADIUS * entry.planet.size;
          if (d < r * 3 && d < this.nearestFlyableDist) {
            this.nearestFlyablePlanet = entry.planet;
            this.nearestFlyableDist = d;
          }
        }

        // ── Focus main planet on the nearest nearby planet ──
        if (this.nearestFlyablePlanet) {
          this.planetStreamer._setFocusedPlanet(this.nearestFlyablePlanet);
        } else if (this.audio.broadcastMode) {
          // Planet radio keeps a source selected even when no planet is nearby.
          this.planetStreamer._setFocusedPlanet(this.currentPlanet);
        } else {
          this.planetStreamer._clearFocusedPlanet();
        }

        // ── Load/unload nearby non-active planets based on hearing range ──
        // Once a planet is no longer audible, unload its detailed mesh.
        for (const entry of this.systemPlanetMeshes) {
          const d = shipPos.distanceTo(entry.worldPos);
          const r = PLANET.SURFACE_RADIUS * entry.planet.size;
          const canHearPlanet = d < r * 3;
          const shouldShowCreatures = canHearPlanet;
          const shouldHearCreatures = canHearPlanet;

          const isFocused = this.focusedPlanet?.index === entry.planet.index;
          const detailMode = isFocused ? 'full' : (canHearPlanet ? 'nearby' : 'marker');

          this.planetStreamer._setPlanetMarkerDetail(entry, detailMode);
          this.planetStreamer._setNearbyPlanetCreatures(entry, shouldShowCreatures);
          this.planetStreamer._setNearbyPlanetAudio(entry, shouldHearCreatures);
        }

        const focusPlanet = this.nearestFlyablePlanet || this.currentPlanet;
        let focusDistance = distToActivePlanet;
        let focusRadius = activePlanetRadius;
        if (focusPlanet.index !== this.currentPlanet.index) {
          const focusEntry = this.planetStreamer._getSystemPlanetEntry(focusPlanet);
          if (focusEntry) {
            focusDistance = shipPos.distanceTo(focusEntry.worldPos);
            focusRadius = PLANET.SURFACE_RADIUS * focusPlanet.size;
          }
        }
        const focusFadeFactor = Math.max(0, Math.min(1,
          (focusDistance - focusRadius * 2) / (focusRadius * 6)
        ));
        this.audio.setDistanceFade(focusFadeFactor);

        // ── Update hint text with landing target ──
        if (this.nearestFlyablePlanet) {
          const pName = this.nearestFlyablePlanet.name;
          this.ui.updateControlsHint(
            `WASD Steer · SPACE Throttle · SHIFT Boost · Q/E Roll · L Land on ${pName} · C Console`
          );
          this.ui.updateActionButton('LAND');
        } else {
          this.ui.updateControlsHint(
            'WASD Steer · SPACE Throttle · SHIFT Boost · Q/E Roll · C Console'
          );
          this.ui.updateActionButton('BOOST');
        }

        // ── Land on nearest planet ──
        if ((this.input.justPressed('KeyL') || this.input.justPressed('Action')) && this.nearestFlyablePlanet) {
          const targetPlanet = this.nearestFlyablePlanet;
          this.shipController.land();

          if (targetPlanet.index !== this.currentPlanet.index) {
            // Flying to a different planet — switch to it then enter ship
            this.planetStreamer._switchToPlanet(targetPlanet).then(() => {
              this._enterShipMode();
              if (this.gameState.radioRepaired) {
                this._showColonizePrompt(targetPlanet);
              }
            });
          } else {
            // Landing back on the same planet — restore full volume
            this.audio.fadeIn(0.5);
            this._enterShipMode();
          }
        }

        // Console
        if (this.input.justPressed('KeyC')) {
          if (!this.gameState.radioRepaired) {
            events.emit(EVT.NOTIFICATION, 'Repair the radio to enable Navigation Console');
          } else {
            this.preMapState = GAME_STATES.FLYING;
            this.input.exitPointerLock();
            this.state = GAME_STATES.GALAXY_MAP;
            events.emit(EVT.MAP_OPEN);
          }
        }
        break;
      }

      case GAME_STATES.GALAXY_MAP: {
        // Waiting for map interaction
        if (this.input.justPressed('Escape') || this.input.justPressed('KeyC')) {
          events.emit(EVT.MAP_CLOSE);
        }

        if (this.input.justPressed('KeyE') || this.input.justPressed('Action')) {
          const selected = this.galaxyMap.getSelectedSystem();
          if (selected) {
            events.emit(EVT.WARP_START, selected);
          } else {
            events.emit(EVT.MAP_CLOSE);
          }
        }
        break;
      }

      case GAME_STATES.WARPING: {
        // Warp animation
        this.warpProgress += dt / SHIP.WARP_DURATION;
        break;
      }

      case GAME_STATES.INTRO: {
        // Waiting for name input — no game updates
        this.input.consumeMouse();
        break;
      }

      case GAME_STATES.JOURNAL: {
        // Overlay is open — consume input, check for close
        this.input.consumeMouse();
        if (this.input.justPressed('Escape') || this.input.justPressed('Tab')) {
          // Close whichever panel is open
          if (this._hudEls.journalPanel && !this._hudEls.journalPanel.classList.contains('hidden')) {
            this._closeJournal();
          } else if (this._hudEls.toolsPanel && !this._hudEls.toolsPanel.classList.contains('hidden')) {
            this._closeToolsPanel();
          }
        }
        break;
      }

      case GAME_STATES.ENDGAME: {
        this.input.consumeMouse();
        this._updateEndgame(dt);
        // Keep creatures alive for ambient sound
        this._updateCreatures(dt);
        // E or Action advances to next phase
        if (this.input.justPressed('KeyE') || this.input.justPressed('Action')) {
          this._advanceEndgameStep();
        }
        break;
      }

      case GAME_STATES.MUSIC_MAKER: {
        this.input.consumeMouse();
        if (this.input.justPressed('Escape')) {
          if (this.ui.isShareDialogOpen()) {
            // Dismiss the share dialog; the awaited save flow then closes the maker
            this.ui.hideShareDialog();
          } else {
            this._closeMusicMaker();
          }
        }
        break;
      }
    }

    // Input frame reset
    this.input.resetFrame();

    // Render
    if (this.composer) {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }

  _onResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    if (this.composer) {
      this.composer.setSize(w, h);
      this.bloomPass.setSize(w, h);
    }
  }
}

// ── Boot ──
const game = new Game();
game.init().catch(err => {
  console.error('Failed to initialize Cosmic Symphony:', err);
  document.querySelector('.loader-status').textContent = 'Error: ' + err.message;
});
